import { supabase } from '../../lib/supabase'
import type { WorkItem } from '../../types'

// ─── Query ─────────────────────────────────────────────
export async function loadWorkItems(organizationId: string) {
    const { data, error } = await supabase
        .from('work_item_financials')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
    if (error) throw error
    return (data || []) as unknown as WorkItem[]
}

// ─── Create ────────────────────────────────────────────
export interface CreateWorkItemInput {
    organization_id: string
    name: string
    type: string
    customer_id?: string
    contract_number?: string
    record_number?: string
    revenue_forecast: number
    revenue_vat_rate: number
    cost_to_complete: number
    end_date?: string
}

export async function createWorkItem(input: CreateWorkItemInput) {
    const { error } = await supabase.from('work_items').insert({
        organization_id: input.organization_id,
        name: input.name,
        type: input.type,
        customer_id: input.customer_id || null,
        status: 'proposed',
        contract_number: input.contract_number || null,
        record_number: input.record_number || null,
        revenue_plan: input.revenue_forecast,
        revenue_forecast: input.revenue_forecast,
        revenue_vat_rate: input.revenue_vat_rate,
        cost_to_complete: input.cost_to_complete,
        end_date: input.end_date || null,
    })
    if (error) throw error
}

// ─── Update ────────────────────────────────────────────
export async function updateWorkItem(id: string, fields: Partial<WorkItem>) {
    const { error } = await supabase.from('work_items').update({
        name: fields.name,
        code: fields.code || null,
        status: fields.status,
        progress: fields.progress,
        revenue_forecast: fields.revenue_forecast,
        revenue_vat_rate: fields.revenue_vat_rate,
        budget_approved: fields.budget_approved,
        cost_to_complete: fields.cost_to_complete,
        contract_number: (fields.contract_number as string)?.trim() || null,
        record_number: (fields.record_number as string)?.trim() || null,
        customer_id: fields.customer_id || null,
        end_date: fields.end_date || null,
        description: fields.description || null,
    }).eq('id', id)
    if (error) throw error
}

// ─── Clone ─────────────────────────────────────────────
export async function cloneWorkItem(original: WorkItem, organizationId: string) {
    const { error } = await supabase.from('work_items').insert({
        organization_id: organizationId,
        name: `${original.name} (bản sao)`,
        type: original.type,
        status: 'draft',
        revenue_plan: original.revenue_plan || 0,
        revenue_forecast: original.revenue_forecast || 0,
        budget_approved: original.budget_approved || 0,
        cost_to_complete: original.cost_to_complete || 0,
        contingency: original.contingency || 0,
        contract_number: null,
        record_number: null,
        progress: 0,
        customer_id: original.customer_id || null,
    })
    if (error) throw error
}

// ─── Close ─────────────────────────────────────────────
export async function closeWorkItem(id: string) {
    const { error } = await supabase.from('work_items').update({ status: 'closed' }).eq('id', id)
    if (error) throw error
}

// ─── Budget Lines ──────────────────────────────────────
export async function addBudgetLine(
    organizationId: string,
    workItemId: string,
    name: string,
    plannedAmount: number,
    categoryId: string
) {
    const { error } = await supabase.from('budget_lines').insert({
        organization_id: organizationId,
        work_item_id: workItemId,
        name,
        planned_amount: plannedAmount,
        category_id: categoryId,
    })
    if (error) throw error
}

export async function removeBudgetLine(id: string, workItemId: string) {
    const { error } = await supabase
        .from('budget_lines')
        .delete()
        .eq('id', id)
        .eq('work_item_id', workItemId)
    if (error) throw error
}
